import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';

import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import ResultView from '../../components/report/ResultView';
import { getSaved, getFields, runSaved, deleteSaved, copySaved } from '../../lib/api/report';
import { parseSelection, describeFilter } from '../../utils/reportSelection';
import { exportReportToExcel } from '../../utils/reportExcelExport';

/** 보고서 보기(/reports/:id) - 항상 지금 보는 사람 범위로 다시 실행한다(R7). */
const ReportViewContainer = ({ rptUuid }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [saved, setSaved] = useState(null);
  const [result, setResult] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoading(true);
    getSaved(rptUuid)
      .then((res) => {
        setSaved(res.data);
        return runSaved(rptUuid, 'VIEW');
      })
      .then((res) => setResult(res.data))
      .catch(() => enqueueSnackbar('보고서를 불러오지 못했습니다.', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [rptUuid, enqueueSnackbar]);

  // 6번(Fable 재검토 2026-10-26): 조건 칩/엑셀 "조건" 시트가 필드 코드 대신 표시명을 쓰도록
  // 필드 목록을 받아온다(FilterBuilder가 작성 화면에서 이미 받아오는 것과 같은 API).
  useEffect(() => {
    if (!saved?.tmplCd) return;
    getFields(saved.tmplCd).then((res) => setFields(res.data || []));
  }, [saved?.tmplCd]);

  const fieldsByCode = useMemo(() => Object.fromEntries(fields.map((f) => [f.fieldCd, f])), [fields]);

  const selection = useMemo(() => (saved ? parseSelection(saved.selJson) : null), [saved]);

  const filtersText = useMemo(() => {
    if (!selection?.filters?.length) return '(없음)';
    return selection.filters.map((f) => describeFilter(f, fieldsByCode)).join(', ');
  }, [selection, fieldsByCode]);

  const handleDelete = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('이 보고서를 삭제하시겠습니까?')) return;
    deleteSaved(rptUuid).then(() => {
      enqueueSnackbar('삭제했습니다.', { variant: 'success' });
      navigate('/reports');
    });
  };

  const handleCopy = () => {
    copySaved(rptUuid).then((res) => {
      enqueueSnackbar('내 보고서로 복사했습니다.', { variant: 'success' });
      navigate(`/reports/${res.data.rptUuid}`);
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await runSaved(rptUuid, 'EXPORT');
      await exportReportToExcel(res.data, {
        tmplNm: saved?.tmplNm,
        rptNm: saved?.rptNm,
        filtersText,
        generatedAt: new Date().toLocaleString(),
      });
    } catch (e) {
      enqueueSnackbar('내보내지 못했습니다.', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  if (loading || !saved) {
    return <Box sx={{ p: 2 }}><Typography color="text.secondary">불러오는 중...</Typography></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <BreadcrumbsContainer title={`분석 보고서/${saved.rptNm}`} icon="chart" />

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6">{saved.rptNm}</Typography>
          <Typography variant="caption" color="text.secondary">
            템플릿: {saved.tmplNm} · 소유자: {saved.ownerUsrNm} · 기준: {new Date().toLocaleString()} · 내 보기 범위로 표시
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<DownloadIcon />} onClick={handleExport} disabled={exporting}>엑셀</Button>
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopy}>내 보고서로 복사</Button>
          {saved.mine && (
            <>
              <Button size="small" startIcon={<EditIcon />} onClick={() => navigate(`/reports/${rptUuid}/edit`)}>편집</Button>
              <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDelete}>삭제</Button>
            </>
          )}
        </Stack>
      </Stack>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
        {selection?.filters?.map((f, i) => (
          <Chip key={i} size="small" label={describeFilter(f, fieldsByCode)} />
        ))}
      </Box>

      <ResultView result={result} viewTyp={saved.viewTyp} gridId={`report-view-${rptUuid}`} />
    </Box>
  );
};

export default ReportViewContainer;
